import type { Key } from 'react'

import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import type { TEquipmentType } from '@web/entities/equipment'
import { EquipmentRoute } from '@web/shared/constants/routes.constant'
import type { EEquipmentContent } from '@web/shared/enums/equipments/equipment-content.enum'
import { EEquipmentMode } from '@web/shared/enums/equipments/equipment-mode.enum'
import { useDispatchedActions } from '@web/shared/hooks/use-dispatched-actions.hook'
import type { TId } from '@web/shared/types/id.type'
import { KEY_ROOT } from '@web/widgets/equipment/equipment-tree/lib/constants'

export interface IEquipmentState {
  typeEquipment: TEquipmentType
  selectedMachineId: TId
  selectedPointId: TId
  selectedMeasurementId: TId
  selectedGroupId: TId
  copyBuffer: {
    copyId: string
    type: typeof EquipmentRoute.Machine | typeof EquipmentRoute.Point
  } | null
  expandedEquipments: Key[]
  draggableMachineId: string | null
  equipmentContent: EEquipmentContent | null
  equipmentMode: EEquipmentMode
}

const initialState: IEquipmentState = {
  typeEquipment: 'group',
  selectedMachineId: null,
  selectedMeasurementId: null,
  selectedPointId: null,
  selectedGroupId: KEY_ROOT,
  copyBuffer: null,
  expandedEquipments: [KEY_ROOT],
  draggableMachineId: null,
  equipmentContent: null,
  equipmentMode: EEquipmentMode.DEFAULT
}

export const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,
  reducers: {
    setTypeEquipment(state, action: PayloadAction<TEquipmentType>) {
      state.typeEquipment = action.payload
    },
    setSelectedMachineId(state, action: PayloadAction<TId>) {
      state.selectedMachineId = action.payload
      state.selectedPointId = null
      state.selectedMeasurementId = null
    },
    setSelectedPointId(state, action: PayloadAction<TId>) {
      state.selectedPointId = action.payload
      state.selectedMeasurementId = null
    },
    setSelectedMeasurementId(state, action: PayloadAction<TId>) {
      state.selectedMeasurementId = action.payload
    },
    setSelectedGroupId(state, action: PayloadAction<TId>) {
      state.selectedGroupId = action.payload
      state.selectedMachineId = null
      state.selectedPointId = null
      state.selectedMeasurementId = null
    },
    resetSelectedForMachine(state) {
      state.selectedMachineId = null
      state.selectedPointId = null
      state.selectedMeasurementId = null
    },
    setExpandedEquipments(state, action: PayloadAction<Key[]>) {
      state.expandedEquipments = action.payload
    },
    toggleExpandedEquipment(state, action: PayloadAction<Key>) {
      const expandedKey = action.payload

      state.expandedEquipments = state.expandedEquipments.includes(expandedKey)
        ? state.expandedEquipments.filter((key) => key !== expandedKey)
        : [...state.expandedEquipments, expandedKey]
    },
    addExpandedEquipments(state, action: PayloadAction<Key[]>) {
      state.expandedEquipments = [
        ...new Set([...state.expandedEquipments, ...action.payload])
      ]
    },
    setEquipmentContent(
      state,
      action: PayloadAction<EEquipmentContent | null>
    ) {
      state.equipmentContent = action.payload
    },
    setDraggableMachineId(state, action: PayloadAction<string | null>) {
      state.draggableMachineId = action.payload
    },
    setCopyBuffer(state, action: PayloadAction<IEquipmentState['copyBuffer']>) {
      state.copyBuffer = action.payload
    }
  }
})

export const equipmentReducer = equipmentSlice.reducer
export const useEquipmentActions = () =>
  useDispatchedActions(equipmentSlice.actions)
